/*
* @ngdoc Service
* @name app.services.UserService
* @description
* This service is used for handling user related functions
*/

import { Injectable } from '@angular/core';
import { CommonHttpService } from './common-http.service';
import { appApiUrl } from '@configs/app-api-urls.config';
import { ApiResponseModel } from '@shared/models/api-response-model';
import { Observable } from 'rxjs';

interface UserLogin {
  email: string;
  password: string;
  remember_me?: boolean;
}

interface CheckEmail {
  email: string;
}

interface RoleAttribute {
  RoleId: string
}

export interface SignUp {
  RoleId: number;
  FirstName: string;
  LastName: string;
  Status: boolean;
  Email: string;
  CompanyName: string;
  CompanyAddress: string;
  PropertyName: string;
  PropertyAddress: string;
  PropertyTypes: any[];
  ServiceTypes: any[];
  Phone: string;
  Password: string;
}


@Injectable()
export class UserService {
  public startedEmail: string;

  constructor(private commonHttpService: CommonHttpService) { }

  /**
   * @function login
   * @description
   * Making a user login request
   * @param param {UserLogin}
   * @returns {Observable<ApiResponseModel>}
   */
  login(param: UserLogin): Observable<ApiResponseModel> {
    return this.commonHttpService.post<ApiResponseModel>(appApiUrl.auth.login, param);
  }

  /**
   * @function checkEmail
   * @description
   * Making a check email request
   * @param param {checkEmail}
   * @returns {Observable<ApiResponseModel>}
   */
  checkEmail(param: any): Observable<ApiResponseModel> {
    return this.commonHttpService.post<ApiResponseModel>(appApiUrl.auth.checkEmail, param);
  }

  /**
   * Update started email 
   * @param Email param
   */
  updateStartedEmail(Email) {
    this.startedEmail = Email;
  }

  /**
   * @function get Roles
   * @description
   * Making a get roles request
   * @returns {Observable<ApiResponseModel>}
   */
  getRoles(): Observable<ApiResponseModel> {
    return this.commonHttpService.get<ApiResponseModel>(appApiUrl.users.role);
  }

  /**
   * @function getRoleAttribute
   * @description
   * Making a check email request
   * @param param {RoleId}
   * @returns {Observable<ApiResponseModel>}
   */
  getRoleAttribute(param: RoleAttribute): Observable<ApiResponseModel> {
    return this.commonHttpService.post<ApiResponseModel>(appApiUrl.attribute, param);
  }

  /**
   * @function login
   * @description
   * Making a user login request
   * @param param {UserLogin}
   * @returns {Observable<ApiResponseModel>}
   */
  signUp(param: any): Observable<ApiResponseModel> {
    return this.commonHttpService.post<ApiResponseModel>(appApiUrl.auth.signup, param);
  }
  /**
   * update user profile
   * @param profileO 
   */
  updateProfile(profileO: any) {
    return this.commonHttpService.post<ApiResponseModel>(appApiUrl.profile.default, profileO);
  }
  /**
   * get user profile
   */
  getProfile() {
    return this.commonHttpService.get<ApiResponseModel>(appApiUrl.profile.default)
  }
  /**
   * change password
   * @param passwordO change passowrd object 
   */
  changePassword(passwordO: any) {
    return this.commonHttpService.post<ApiResponseModel>(appApiUrl.auth.changePassword, passwordO);
  }
  /**
   * confirm email
   * @param token 
   */
  confirmEmail(token: string) {
    return this.commonHttpService.get<ApiResponseModel>(`${appApiUrl.auth.confirmEmail}?token=${token}`)
  }
  /**
   * pay user subscription
   * @param subsO 
   */
  userSubscription(subsO: any) {
    return this.commonHttpService.post<ApiResponseModel>(appApiUrl.users.subscription, subsO);
  }

  /**
   * user upgrade subscription 
   * @param subsO 
   * @returns 
   */
  userUpgradeSubscription(subsO: any) {
    return this.commonHttpService.post<ApiResponseModel>(appApiUrl.users.changeSubscription, subsO);
  }

  /**
   * buy subscription for discounted and paid
   * @param subsO 
   * @returns 
   */
  subscriptionDiscountedPaid(subsO: any) {
    return this.commonHttpService.post<ApiResponseModel>(appApiUrl.users.subscriptionDiscountedPaid, subsO);
  }
  /**
   * update card info
   * @param cardO 
   */
  updateCard(cardO: any) {
    return this.commonHttpService.post<ApiResponseModel>(appApiUrl.users.updateCard, cardO);
  }
  /**
   * get user card
   */
  getUserCard() {
    return this.commonHttpService.get<ApiResponseModel>(appApiUrl.users.card);
  }
  /**
   * get user plan
   */
  getUserBillingInfo() {
    return this.commonHttpService.get<ApiResponseModel>(appApiUrl.users.billingInfo);
  }
  /**
   * send forgot pass request
   * @param fPassO 
   */
  forgotPassword(fPassO: any) {
    return this.commonHttpService.post<ApiResponseModel>(appApiUrl.auth.forgotPass, fPassO);
  }
  /**
   * reset password
   * @param rPassO 
   */
  resetPassword(rPassO: any) {
    return this.commonHttpService.post<ApiResponseModel>(appApiUrl.auth.resetPass, rPassO);
  }
  /**
   * validate reset token
   * @param token 
   */
  validateResetToken(token: string) {
    return this.commonHttpService.get<ApiResponseModel>(`${appApiUrl.auth.validateToken}?token=${token}`)
  }
  /**
   * get plan quota detail for analytics
   */
  getPlanQuotaDetail() {
    return this.commonHttpService.get<ApiResponseModel>(appApiUrl.users.planQuotaDetail)
  }

  /**
   * connect and save facebook token
   * @param token 
   * @returns 
   */
  fbconnect(token: string) {
    return this.commonHttpService.get<ApiResponseModel>(`${appApiUrl.broadCastSetting.facebook}?token=${token}`)
  }

  /**
   * connect and save youtube token
   * @param token 
   * @returns 
   */
  ytConnect(token: string) {
    return this.commonHttpService.get<ApiResponseModel>(`${appApiUrl.broadCastSetting.google}?token=${token}`)
  }

  /**
   * linkedin connect
   * @param token 
   * @returns 
   */
  lNConnect(token: string) {
    return this.commonHttpService.get<ApiResponseModel>(`${appApiUrl.broadCastSetting.lnConnect}?token=${token}`)
  }

  /**
   * get token for socail platforms
   * @param tokenType 
   * @returns 
   */
  getToken(tokenType: string) {
    return this.commonHttpService.get<ApiResponseModel>(`${appApiUrl.broadCastSetting.token}/${tokenType}`)
  }

  /**
   * get subscription information of user
   * @returns 
   */
  getSubscriptionInfo() {
    return this.commonHttpService.get<ApiResponseModel>(appApiUrl.users.subscription)
  }

  /**
   * disconnect social account 
   * @param tokenType 
   * @returns 
   */
  logoutSn(tokenType: string) {
    return this.commonHttpService.get<ApiResponseModel>(`${appApiUrl.broadCastSetting.disconnect}/${tokenType}`)
  }

  /**
   * get timezone of user
   * @returns 
   */
  getTimezone() {
    return this.commonHttpService.get<ApiResponseModel>(appApiUrl.users.timezone)
  }

  /**
   * get conference setting
   * @returns 
   */
  getConferenceSetting() {
    return this.commonHttpService.get<ApiResponseModel>(appApiUrl.confSetting.default)
  }

  /**
   * save or update conference setting 
   * @param setting 
   * @returns 
   */
  saveUpdateConferenceSetting(setting: any) {
    return this.commonHttpService.post<ApiResponseModel>(appApiUrl.confSetting.default, setting);
  }

  /**
   * get user referral code
   * @returns 
   */
  getUserReferralCode() {
    return this.commonHttpService.get<ApiResponseModel>(appApiUrl.users.referralCode);
  }

  /**
   * save broadcast setting 
   * @param broadCastSetting 
   * @returns 
   */
  saveBroadCastSetting(broadCastSetting: any) {
    return this.commonHttpService.post<ApiResponseModel>(appApiUrl.broadCastSetting.default, broadCastSetting);
  }

  /**
   * get broadcast setting 
   * @param sessionId 
   * @returns 
   */
  getBroadCastSetting(sessionId: string) {
    return this.commonHttpService.get<ApiResponseModel>(`${appApiUrl.broadCastSetting.getBySessionId}/${sessionId}`);
  }

  /**
   * check whether facebook app is installed on group
   * @param groupId 
   * @returns 
   */
  isAppInstalledInGroup(groupId: string) {
    return this.commonHttpService.get<ApiResponseModel>(`${appApiUrl.broadCastSetting.isAppInstalledInGroup}/${groupId}`);
  }

  /**
   * get refresh token
   * @returns 
   */
  refreshToken() {
    return this.commonHttpService.get<ApiResponseModel>(`${appApiUrl.auth.refreshToken}`);
  }

  /**
   * get user information 
   * @returns 
   */
  getUserInfo() {
    return this.commonHttpService.get<ApiResponseModel>(appApiUrl.users.userInfo);
  }

  /**
   * update user on boarded tour
   * so use will not see tour again
   * @returns 
   */
  updateUserOnBoarded() {
    return this.commonHttpService.get<ApiResponseModel>(appApiUrl.users.onBoarding);
  }

  /**
   * get invoices
   * @returns 
   */
  getInvoices() {
    return this.commonHttpService.get<ApiResponseModel>(appApiUrl.users.invoices);
  }

  /**
   * buy additonal storages
   * @returns 
   */
  buyAddonStorage() {
    return this.commonHttpService.get<ApiResponseModel>(appApiUrl.users.buyStorageAddon);
  }

  /**
   * unsubscribe subscription 
   * @returns 
   */
  unsubscribe() {
    return this.commonHttpService.get<ApiResponseModel>(appApiUrl.users.unsubscribe);
  }

  /**
   * get cancellation options
   * @returns 
   */
  getCancellationOptions() {
    return this.commonHttpService.get<ApiResponseModel>(appApiUrl.users.cancellationOptions);
  }

  /**
   * cancel subscription 
   * @param cancelObj 
   * @returns 
   */
  cancelSubscription(cancelObj: any) {
    return this.commonHttpService.post<ApiResponseModel>(appApiUrl.users.subscriptionCancellation, cancelObj);
  }

  /**
   * reactive susbcription
   * @returns 
   */
  reactivateSubscription() {
    return this.commonHttpService.get<ApiResponseModel>(appApiUrl.users.subscriptionReactivate);
  }

  /**
   * remove scheduled subscription cancellation
   * @returns 
   */
  removeScheduledSubscriptionCancellation() {
    return this.commonHttpService.get<ApiResponseModel>(appApiUrl.users.subscriptionRemove);
  }


  googleSignIn(param: { token: string }): Observable<ApiResponseModel> {
    return this.commonHttpService.post<ApiResponseModel>(appApiUrl.auth.googleSignIn, param);
  }
}
